import {AfterViewInit, Component, Input, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {AuditHistoryGroupModel} from "../../../services/audit-history/audit-history-group.model";
import {
  CheckBoxSelectionService,
  MultiSelectComponent, PopupEventArgs,
  RemoveEventArgs, SelectEventArgs, ToolbarSettingsModel
} from "@syncfusion/ej2-angular-dropdowns";
import {
  ISelectAllEventArgs,
} from "@syncfusion/ej2-dropdowns/src/multi-select/multi-select";
import {ButtonPropsModel} from "@syncfusion/ej2-popups/src/dialog/dialog-model";
import {DialogComponent} from "@syncfusion/ej2-angular-popups";

import {cloneDeep} from 'lodash';
import * as fastDeepEqual from 'fast-deep-equal/es6';
import {ListBoxComponent} from "@syncfusion/ej2-angular-dropdowns/src/list-box/listbox.component";
import { $translate } from 'src/app/core/services/internationalization/localize.service';

@Component({
  selector: 'sb-audit-history-journal-filter',
  templateUrl: './audit-history-journal-filter.component.html',
  styleUrls: ['./audit-history-journal-filter.component.scss'],
  providers: [CheckBoxSelectionService]
})
export class AuditHistoryJournalFilterComponent implements OnInit, AfterViewInit {

  TOGGLE_STORAGE_KEY = "audit-history-journal-filter-toggle-state";

  FIELD_STORAGE_KEY = "audit-history-journal-filter-field-state";

  SLA_FIELDS = ['SLAStatus', 'SLANextDue', 'SLANextDueRecord', 'SLANextThresholdDue', 'SLANextThresholdRecord'];

  WORKFLOW_FIELDS = ['WorkflowStatus', 'PreviousWorkflowStatus', 'PreviousTransition'];

  SYSTEM_FIELDS = ['CreatedBy', 'CreatedOn', 'UpdatedBy', 'UpdatedOn', 'Version', 'TableClass', 'SID'];

  INITIALLY_UNSELECTED_FIELDS = ['WorkJournal'];

  JOURNAL_FILTER_ALL = 'journal-filter-all';

  JOURNAL_FILTER_NONE = 'journal-filter-none';

  JOURNAL_FILTER_NOTES = 'journal-filter-notes';

  JOURNAL_FILTER_FIELD_CHANGES = 'journal-filter-field-changes';

  JOURNAL_FILTER_WORKFLOW_CHANGES = 'journal-filter-workflow-changes';

  JOURNAL_FILTER_SLA_CHANGES = 'journal-filter-sla-changes';

  defaultToggleState = Object.fromEntries([
    [this.JOURNAL_FILTER_NOTES, true],
    [this.JOURNAL_FILTER_FIELD_CHANGES, false],
    [this.JOURNAL_FILTER_WORKFLOW_CHANGES, false],
    [this.JOURNAL_FILTER_SLA_CHANGES, false]
  ]);

  currentToggleState = Object.fromEntries([
    [this.JOURNAL_FILTER_NOTES, true],
    [this.JOURNAL_FILTER_FIELD_CHANGES, false],
    [this.JOURNAL_FILTER_WORKFLOW_CHANGES, false],
    [this.JOURNAL_FILTER_SLA_CHANGES, false]
  ]);

  tableName: string = "";

  // Callback set by KO which is called when the state changes in the filter.
  @Input()
  auditHistoryFilterStateChangeCallback: any;

  // Called by KO to pass in the record for which we find the audit history.
  @Input()
  set auditHistoryRecord(record: any) {
    this.tableName = this.getTableName(record);
    this.buildFieldFilterList(record);
    this.applyStateToToggles(this.retrieveToggleState() || this.defaultToggleState);
  };

  // Return a thunk that applies the filter to the group
  @Input()
  get filter() {
    const self = this;
    return (group: AuditHistoryGroupModel) => {
      return self.filterGroup(group);
    };
  }

  // field filter support

  @ViewChild('ejFieldFilter', {static: false})
  public fieldFilter: MultiSelectComponent;

  // All fields that could appear in the field filter
  public allFields: string[] = [];

  // All fields that do appear in the field filter
  public fields: string[] = [];

  // Set fields in the field filter
  public setFields: string[] = [];

  // Fields in the field filter and whether they are set or not. This is persisted.
  public fieldState: {[key: string]: boolean} = {};

  // Changes in this object are the result of manipulation of field filter
  public pendingFieldState: {[key: string]: boolean} = {};

  public filterPlaceholder: string = $translate("ng.component.audit-history-journal-filter.search")`Search fields`;

  public popupHeight: string = '350px';

  public popupWidth: string = '350px';

  // field filter dialog support

  @ViewChild('ejFieldFilterDialog', {static: false}) ejDialog: DialogComponent;

  @ViewChild('ejAvailableFields', {static: false}) ejAvailableFields: ListBoxComponent;

  @ViewChild('ejIncludedFields', {static: false}) ejIncludedFields: ListBoxComponent;

  public visible = false;

  public position: any = { X: 'center', Y: 'center' };

  public buttons: ButtonPropsModel[] = [
    {
      'click': this.hideDialog.bind(this, false),
      buttonModel: {
        content: $translate("ng.component.audit-history-journal-filter.configure.cancel")`Cancel`,
      }
    },
    {
      'click': this.hideDialog.bind(this, true),
      buttonModel: {
        content: $translate("ng.component.audit-history-journal-filter.configure.save")`Save`,
        isPrimary: true,
      }
    },
  ];

  title: string = $translate("ng.component.audit-history-journal-filter.configure.title")`Select included fields`;

  public toolbarSettings: ToolbarSettingsModel = { items: ['moveTo', 'moveFrom', 'moveAllTo', 'moveAllFrom']};

  // Fields that should appear in the field filter
  public includedFields = [];

  // Fields that should not appear in the field filter
  public availableFields = [];

  constructor() { }

  ngOnInit(): void {
  }

  ngAfterViewInit() {
  }

  // Filter a group based on the current filter, return null if the entire group should be removed
  // Note that the group might actually be an individual note (as the filter also does notes), so the
  // signature is a bit dodgy.
  filterGroup(group: AuditHistoryGroupModel): AuditHistoryGroupModel {
    if (!group.AuditedBy) {
      // This is actually a note. Check to see if notes are filtered out.
      return this.currentToggleState[this.JOURNAL_FILTER_NOTES] ? group : null;
    } else {
      // This is a group.
      const currentRecords = group.records;
      const newRecords = [];
      currentRecords.forEach((record) => {
        if (this.WORKFLOW_FIELDS.includes(record.Field.displayValue)) {
          if (this.currentToggleState[this.JOURNAL_FILTER_WORKFLOW_CHANGES]) {
            newRecords.push(record);
          }
        } else if (this.SLA_FIELDS.includes((record.Field.displayValue))) {
          if (this.currentToggleState[this.JOURNAL_FILTER_SLA_CHANGES]) {
            newRecords.push(record);
          }
        } else {
          if (this.currentToggleState[this.JOURNAL_FILTER_FIELD_CHANGES]) {
            if (this.fieldState[record.Field.displayValue]) {
              newRecords.push(record);
            }
          }
        }
      });

      group.records = newRecords;

      return newRecords.length > 0 ? group : null;
    }
  }

  // Support for the overall filter

  toggleInputFilterGlobal($event) {
    const value = $event.target.id === this.JOURNAL_FILTER_ALL;
    Object.keys(this.currentToggleState).forEach((key) => {
      this.currentToggleState[key] = value;
    })
    this.applyStateToToggles(this.currentToggleState);

    const checkboxes = document.querySelectorAll(".journal-filter-global");
    checkboxes.forEach((element) => {
      (element as HTMLInputElement).checked = false;
    });
  }

  toggleInputFilter($event) {
    this.currentToggleState[$event.target.id] = !this.currentToggleState[$event.target.id];
    this.applyStateToToggles(this.currentToggleState);
  }

  private applyStateToToggles(state: any) {
    const checkboxes = document.querySelectorAll(".journal-filter-checkboxes");
    checkboxes.forEach((element) => {
      const checkbox = element as HTMLInputElement;
      checkbox.checked = state[checkbox.id];
    });

    this.storeToggleState();

    this.callAuditHistoryFilterStateChangeCallback();
  }

  private callAuditHistoryFilterStateChangeCallback() {
    const auditRecordsRequired =
      this.currentToggleState[this.JOURNAL_FILTER_FIELD_CHANGES] ||
      this.currentToggleState[this.JOURNAL_FILTER_WORKFLOW_CHANGES] ||
      this.currentToggleState[this.JOURNAL_FILTER_SLA_CHANGES];

    this.auditHistoryFilterStateChangeCallback && this.auditHistoryFilterStateChangeCallback(auditRecordsRequired);
  }

  private storeToggleState() {
    const key = `${this.TOGGLE_STORAGE_KEY}.${this.tableName}`;
    sessionStorage.setItem(key, JSON.stringify(this.currentToggleState));
    localStorage.setItem(key, JSON.stringify(this.currentToggleState));
  }

  private retrieveToggleState(): any {
    const key = `${this.TOGGLE_STORAGE_KEY}.${this.tableName}`;
    const state = sessionStorage.getItem(key) || localStorage.getItem(key);
    if (state) {
      this.currentToggleState = JSON.parse(state);
      return this.currentToggleState;
    } else {
      return null;
    }
  }

  private storeFieldState() {
    const key = `${this.FIELD_STORAGE_KEY}.${this.tableName}`;
    sessionStorage.setItem(key, JSON.stringify(this.fieldState));
    localStorage.setItem(key, JSON.stringify(this.fieldState));
  }

  private retrieveFieldState(): any {
    const key = `${this.FIELD_STORAGE_KEY}.${this.tableName}`;
    const state = sessionStorage.getItem(key) || localStorage.getItem(key);
    if (state) {
      this.fieldState = JSON.parse(state);
      return this.fieldState;
    } else {
      return null;
    }
  }

  // Support for the field level filter

  onOpen(event: PopupEventArgs) {

    // By setting the position to this, we force the mutliselect positioning algorithm down a different
    // code path thus avoiding SER-3287.
    event.popup.position = {
      X: 'right', // 'left', 'center', 'right' -> default is left
      Y: 'bottom', // 'top', 'center', 'bottom' -> default is bottom
    };

    // Collision can stay as the default
    // $event.popup.collision = {
    //   X: 'flip', // 'none', 'flip', 'fit' -> default is flip
    //   Y: 'flip', // 'none', 'flip', 'fit' -> default is flip
    // };

    // We set offsetY to a small value so that the popup doesn't cover the buttons when flipped to avoid
    // a collision.
    // $event.popup.offsetX = 0;
    event.popup.offsetY = 5;

    this.pendingFieldState = cloneDeep(this.fieldState);
  }

  onSelect(event: SelectEventArgs) {
    this.pendingFieldState[event.itemData as string] = true;
  }

  onRemoved(event: RemoveEventArgs) {
    this.pendingFieldState[event.itemData as string] = false;
  }

  onSelectedAll(event: ISelectAllEventArgs) {
    const value = event.isChecked;

    Object.keys(this.fieldState).forEach((key) => {
      this.pendingFieldState[key] = value;
    })
  }

  onClose(_: PopupEventArgs) {
    if (!fastDeepEqual(this.fieldState, this.pendingFieldState)) {
      this.fieldState = cloneDeep(this.pendingFieldState);
      this.pendingFieldState = {};
      this.storeFieldState();
      this.callAuditHistoryFilterStateChangeCallback();
    }
  }

  private buildFieldFilterList(record: any) {

    this.allFields = Object.keys(record).filter((key) => {
      return !(this.SYSTEM_FIELDS.includes(key) ||
        this.WORKFLOW_FIELDS.includes(key) ||
        this.SLA_FIELDS.includes(key) ||
        key.match(/^[a-z_].*$/));
    });

    const initialFieldState = this.allFields.reduce((acc, curr) => {
      acc[curr] = !this.INITIALLY_UNSELECTED_FIELDS.includes(curr);
      return acc;
    }, {});

    this.fieldState = this.retrieveFieldState() || initialFieldState;

    this.fields = Object.entries(this.fieldState).map(([key,value]) => key);

    this.setFields = Object.entries(this.fieldState).filter(([key,value]) => value === true).map(([key,value]) => key);
  }

  private getTableName(record: any): string {
    return record._tableClass();
  }

  public showDialog() {

    // Build included fields and available fields by looking at allFields and fields
    this.includedFields = cloneDeep(this.fields);
    this.availableFields = this.allFields.filter((field) => !this.includedFields.includes(field));

    this.ejDialog.show();
  }

  public hideDialog(save: boolean) {
    this.ejDialog.hide();

    if (save) {
      const pendingIncludedFields = this.ejIncludedFields.getDataList() as string[];
      const pendingAvailableFields = this.ejAvailableFields.getDataList() as string[];

      // Copy the current field state if it is in pending included fields
      const newFieldState = Object.fromEntries(Object.entries(this.fieldState).filter(([key, value]) => pendingIncludedFields.includes(key)));

      // Create new entries
      pendingIncludedFields.forEach((key) => {
        if (!newFieldState[key]) {
          newFieldState[key] = true;
        }
      });

      this.fieldState = newFieldState;

      this.fields = Object.entries(this.fieldState).map(([key, value]) => key);
      this.setFields = Object.entries(this.fieldState).filter(([key, value]) => value === true).map(([key, value]) => key);

      this.storeFieldState();
      this.callAuditHistoryFilterStateChangeCallback();
    }
  }

  onOpenDialog(_: MouseEvent) {
    this.showDialog();
    return false;
  }

}
